/** The darkness every overlay had before it was adjustable; overlays are designed at this value. */
const DEFAULT_OVERLAY_DARKNESS = 50;

/** Never fully opaque: the photo must still show through. */
const MAX_ALPHA = 0.95;

/** Opacity is a fraction; CSS percentages and rounding to two places use this. */
const PERCENT = 100;

/** The Light, Medium and Strong buttons that came before the slider, as slider positions. */
const LEGACY_STRENGTH_DARKNESS: Partial<Record<string, number>> = { light: 30, medium: 50, strong: 65 };

/**
 * How much to multiply an overlay's designed opacity by.
 * @param {number | undefined} darkness The slider value, 0 to 100; the designed look when unset.
 * @returns {number} 0 at 0, 1 at the default, 2 at 100.
 */
const overlayScale = (darkness?: number): number => Math.max(0, darkness ?? DEFAULT_OVERLAY_DARKNESS) / DEFAULT_OVERLAY_DARKNESS;

/**
 * An opacity scaled for a darkness, capped so the photo shows through.
 * @param {number} alpha The designed opacity, 0 to 1.
 * @param {number} scale From overlayScale.
 * @returns {number} The opacity to paint, to two places.
 */
const scaleAlpha = (alpha: number, scale: number): number => Math.round(Math.min(alpha * scale, MAX_ALPHA) * PERCENT) / PERCENT;

/**
 * A color-mix() percentage for a designed opacity at a darkness.
 * @param {number} alpha The designed opacity, 0 to 1.
 * @param {number} scale From overlayScale.
 * @returns {number} The whole percentage to mix in.
 */
const scalePercent = (alpha: number, scale: number): number => Math.round(scaleAlpha(alpha, scale) * PERCENT);

/**
 * The darkness to paint: the slider's value, else the old Light/Medium/Strong choice, else the default.
 * @param {{ darkness?: number; legacyStrength?: string }} saved The slider value, and any Light/Medium/Strong value saved before the slider.
 * @returns {number} The darkness, 0 to 100.
 */
const resolveOverlayDarkness = ({ darkness, legacyStrength }: { darkness?: number; legacyStrength?: string }): number =>
  darkness ?? LEGACY_STRENGTH_DARKNESS[legacyStrength ?? ""] ?? DEFAULT_OVERLAY_DARKNESS;

export { DEFAULT_OVERLAY_DARKNESS, overlayScale, resolveOverlayDarkness, scaleAlpha, scalePercent };
